import { makeAutoObservable, runInAction, observable } from "mobx";
import { IAccountStore, Store, AllUserData, CustomerDetails } from "../global/types";
import { getAllCustomersData, getCustomerDetails, updateCustomerDetails } from "../api/user.action"
import { toastManager } from "../utils/toastManager";

class AccountStore implements IAccountStore {
    rootStore: Store;
    allCustomersData: AllUserData[] = [];
    customerData: AllUserData | null = null;
    customerProfile: CustomerDetails | null = null;
    error: string | null = null;
    loading: boolean = false;
    page: number = 1;
    searchTerm: string = '';
    limit: number = 10;
    subscribeStatus: string = '';
    mockSubscriptionStatus: string = ''; 
    registrationFilter: string = 'registered';
    totalItems: number = 0;
    totalPages: number = 0;
    registeredCount: number = 0;
    nonRegisteredCount: number = 0;
    customerId: string = '';
    startDate: string | Date = '';
    endDate: string | Date = '' ;

    constructor(rootStore: Store) {
        makeAutoObservable(this, {
          allCustomersData: observable.shallow
        });
        this.rootStore = rootStore;
    }

    setLoading = (loading: boolean) => {
        this.loading = loading;
    }

    fetchAllCustomersData = async (
        action: 'change_page' | 'search' | 'change_limit' | 'change_filter' | 'change_registration_filter' | '',
        page: number,
        searchTerm: string,
        limit: number,
        subscribeStatus: string,
        mockSubscriptionStatus: string,
        registrationFilter?: string,
    ) => {
        this.setLoading(true)
        try {
            runInAction(() => {
                switch (action) {
                    case 'change_page':
                        this.page = page;
                        break;
                    case 'search':
                        this.searchTerm = searchTerm;
                        this.page = 1;
                        break;
                    case 'change_limit':
                        this.limit = limit;
                        this.page = 1
                        break;
                    case 'change_filter':
                        this.subscribeStatus = subscribeStatus;
                        this.mockSubscriptionStatus = mockSubscriptionStatus
                        this.page = 1;
                        break;
                    case 'change_registration_filter':
                        this.registrationFilter = registrationFilter ?? '';
                        this.page = 1;
                        break;
                    default:
                        this.subscribeStatus = ''
                        this.mockSubscriptionStatus = ''
                        break;
                }
            })
            const data = {
                page: this.page,
                limit: this.limit,
                search: this.searchTerm?.trim(),
                subscribeStatus: this.subscribeStatus,
                mockSubscriptionStatus: this.mockSubscriptionStatus,
                registrationFilter: this.registrationFilter
            }
            const response = await getAllCustomersData(data);
            runInAction(() => {
                this.allCustomersData = response.data?.users || [];
                this.totalItems = response.data?.totalItems;
                this.totalPages = response.data?.totalPages;
                // derive counts for both tabs over time using totalItems of each active tab
                if (this.registrationFilter === 'registered') {
                    this.registeredCount = this.totalItems;
                } else if (this.registrationFilter === 'non_registered') {
                    this.nonRegisteredCount = this.totalItems;
                }
            })
        } catch (error: unknown) {
            this.rootStore.authStore.handleUnauthorized(error);
        } finally {
            this.setLoading(false);
        }
    }

    fetchOneCustomerData = async (id: string) => {
        this.setLoading(true);
        try {
            const response = await getCustomerDetails(id)
            runInAction(() => {
                this.customerProfile = response.data.details
                this.customerData = response.data.profile
                this.rootStore.alarmStore.allAlarmsData = response.data.alarms
                this.rootStore.leagueStore.allLeaguesData = response.data.leagues
                this.rootStore.motiTagStore.allMotiTagsData = response.data.motiTags
            })
        } catch (error: unknown) {
            this.rootStore.authStore.handleUnauthorized(error);
        } finally {
            this.setLoading(false);
        }
    }

    async updateCustomerData(userId: string, newDetails: Partial<AllUserData>) {
        try {
            await updateCustomerDetails(userId, newDetails);
        } catch (err) {
            toastManager.handleError(err);
        }
    }

    resetCustomerPageDataStates = () => {
        runInAction(() => {
            this.allCustomersData = [];
            this.customerData = null;
            this.customerProfile = null;
            this.error = null;
            this.loading = false;
            this.page = 1;
            this.searchTerm = '';
            this.limit = 10;
            this.totalItems = 0;
            this.totalPages = 0;
            this.customerId = '';
            this.subscribeStatus = '';
            this.mockSubscriptionStatus = '';
            this.registrationFilter = 'registered';
        })
    }
}

export default AccountStore;
